<template>
  <div class="mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Commission Tracking</h1>
      <div class="flex space-x-3">
        <button
          @click="enrichAllCustomers"
          :disabled="enrichLoading"
          class="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <i class="fas fa-users mr-2" :class="{ 'animate-spin': enrichLoading }"></i>
          {{ enrichLoading ? 'Enriching...' : 'Fetch All Customers' }}
        </button>
        <button
          @click="manualSync"
          :disabled="syncLoading"
          class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <i class="fas fa-sync-alt mr-2" :class="{ 'animate-spin': syncLoading }"></i>
          {{ syncLoading ? 'Syncing...' : 'Sync Stripe' }}
        </button>
        <a :href="routes.create" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
          <i class="fas fa-plus mr-2"></i> Add Manual Transaction
        </a>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-yellow-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-xs font-medium text-yellow-600 uppercase tracking-wide">
              Unmapped Commissions
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ stats.total_unmapped }}</div>
            <div class="text-xs text-gray-500">{{ formatCurrency(stats.unmapped_value) }} EUR <span class="text-xxs">(net, no VAT)</span></div>
            <div class="text-xxs text-gray-400 mt-1">
              Premier: {{ stats.premier_partner_unmapped || 0 }} ({{ formatCurrency(stats.premier_partner_unmapped_value || 0) }})
              <br>Business: {{ stats.business_partner_unmapped || 0 }} ({{ formatCurrency(stats.business_partner_unmapped_value || 0) }})
            </div>
          </div>
          <div class="ml-4">
            <i class="fas fa-exclamation-triangle text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-blue-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-xs font-medium text-blue-600 uppercase tracking-wide">
              Mapped (Unpaid)
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ stats.mapped_unpaid }}</div>
            <div class="text-xs text-gray-500">{{ formatCurrency(stats.mapped_unpaid_value) }} EUR <span class="text-xxs">(net, no VAT)</span></div>
          </div>
          <div class="ml-4">
            <i class="fas fa-check-circle text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-green-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-xs font-medium text-green-600 uppercase tracking-wide">
              Total Transactions
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ stats.total_transactions || 0 }}</div>
            <div class="text-xs text-gray-500">
              Net: {{ formatCurrency(stats.total_transaction_value_net || 0) }} EUR
              <br><span class="text-xxs">Gross: {{ formatCurrency(stats.total_transaction_value_gross || 0) }} EUR</span>
            </div>
          </div>
          <div class="ml-4">
            <i class="fas fa-chart-line text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-purple-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-xs font-medium text-purple-600 uppercase tracking-wide">
              Total Commissions
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ formatCurrency(stats.total_commissions_net || 0) }}</div>
            <div class="text-xs text-gray-500">EUR <span class="text-xxs">(net, no VAT)</span></div>
          </div>
          <div class="ml-4">
            <i class="fas fa-money-bill-wave text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-card shadow-xl mb-8">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Filters</h2>
      </div>
      <div class="p-6">
        <form @submit.prevent="applyFilters" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
          <div>
            <label for="status" class="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select v-model="localFilters.status" id="status" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">All</option>
              <option value="unmapped">Unmapped</option>
              <option value="mapped">Mapped</option>
            </select>
          </div>

          <div>
            <label for="company_id" class="block text-xs font-medium text-gray-700 mb-1">Company</label>
            <select v-model="localFilters.company_id" id="company_id" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">All Companies</option>
              <option v-for="company in companies" :key="company.id" :value="company.id">
                {{ company.name }}
              </option>
            </select>
          </div>

          <div>
            <label for="from_date" class="block text-xs font-medium text-gray-700 mb-1">From</label>
            <input v-model="localFilters.from_date" type="date" id="from_date" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          </div>

          <div>
            <label for="to_date" class="block text-xs font-medium text-gray-700 mb-1">To</label>
            <input v-model="localFilters.to_date" type="date" id="to_date" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          </div>

          <div>
            <label for="search" class="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <input
              v-model="localFilters.search"
              type="text"
              id="search"
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Email, name, or Stripe ID"
            >
          </div>

          <div class="flex items-end">
            <label class="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700">
              <input
                v-model="localFilters.reportable_only"
                type="checkbox"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              >
              <span>Reportable only</span>
            </label>
          </div>

          <div class="flex items-end space-x-2">
            <button type="submit" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">Filter</button>
            <button type="button" @click="clearFilters" class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="bg-white rounded-card shadow-xl">
      <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 class="text-lg font-semibold text-gray-900">{{ transactions.length === 1 ? 'Showing single transaction' : 'Transactions' }}</h2>
          <a :href="route('admin.commission-tracking')" class="mf-link">
              Show all
          </a>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Stripe ID</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Amount (Net/Gross)</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Product</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Transaction Source</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Premier Partner</th>
              <th class="px-6 py-3 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider">Business Partner</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(transaction, index) in transactions"
              :key="transaction.id"
              :class="[
                transaction.auto_mapped ? 'bg-green-50' : '',
                activeRowIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
              @click="setActiveRow(index)"
              class="cursor-pointer"
            >
              <td class="px-6 py-3 whitespace-nowrap text-xs text-gray-900">
                  <a
                      :href="route('admin.commission-tracking', {transaction_id: transaction.id})"
                      target="_blank"
                      class="mf-link text-xxs"
                  >
                    {{ formatDate(transaction.transaction_date) }}
                  </a>
                  <div v-if="transaction.status" class="text-xxs" :class="{
                      'text-green-600': transaction.status === 'completed',
                      'text-yellow-600': transaction.status === 'pending',
                      'text-red-600': transaction.status === 'failed'
                    }">
                      {{ transaction.status }}
                  </div>
                  <div v-if="transaction.stripe_id" class="mt-1">
                      <span
                          v-if="transaction.stripe_sale_key"
                          class="inline-flex items-center rounded bg-green-100 px-1 py-px text-xxs font-medium text-green-700"
                          :title="`Sale key: ${transaction.stripe_sale_key}`"
                      >
                        Backfilled
                      </span>

                      <span
                          v-else
                          class="inline-flex items-center rounded bg-yellow-100 px-1 py-px text-xxs font-medium text-yellow-700"
                      >
                        Not backfilled
                      </span>
                  </div>
                  <span
                      class="inline-flex items-center rounded px-1 py-px text-xxs font-medium mt-1"
                      :class="isReportableSale(transaction) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                  >
                    {{ isReportableSale(transaction) ? 'Reportable' : 'Not reportable' }}
                  </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div class="font-mono text-xxs text-gray-600">{{ transaction.stripe_id || 'N/A' }}</div>
                <div v-if="transaction.id" class="text-xxs space-x-2">
                    <span class="text-gray-400">ID: {{ transaction.id }}</span>
                    <a :href="'https://dashboard.stripe.com/acct_1OFy5rGNaIYhKUhu/payments/search?query='+transaction.stripe_id" target="_blank" class="mf-link">Open in Stripe</a>
                </div>
                <button
                  v-if="transaction.stripe_id"
                  @click.stop="backfillSale(transaction)"
                  :disabled="isBackfillingSale(transaction)"
                  class="mt-2 inline-flex items-center text-xxs text-blue-600 hover:text-blue-800 disabled:text-blue-300 underline"
                  title="Backfill this Stripe sale and hide duplicate rows from reporting"
                >
                  <i
                    class="fas fa-sync-alt mr-1"
                    :class="{ 'animate-spin': isBackfillingSale(transaction) }"
                  ></i>
                  {{ isBackfillingSale(transaction) ? 'Backfilling...' : 'Backfill sale' }}
                </button>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs text-gray-900">
                <div class="font-medium">
                  <span class="text-green-600">Net: {{ formatAmount(transaction.net_amount_cents, transaction.currency) }}</span>
                  <span class="text-gray-400"> / </span>
                  <span class="text-gray-500">Gross: {{ formatAmount(transaction.amount_cents, transaction.currency) }}</span>
                </div>
                <div class="text-xxs text-gray-500 mt-1">
                  Commissions: {{ formatAmount(getTotalCommissionAmount(transaction), transaction.currency) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div v-if="transaction.company" class="flex items-center">
                  <a
                    :href="getCompanyViewUrl(transaction.company.id)"
                    target="_blank"
                    class="mf-link font-medium"
                  >
                    {{ transaction.company.name }}
                  </a>
                  <span v-if="transaction.auto_mapped" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xxs font-medium bg-green-100 text-green-800">
                    Auto
                  </span>
                </div>
                <div v-else class="flex items-center space-x-2">
                  <span class="text-red-600 font-medium">Unmapped Company</span>
                  <button
                    @click="showCompanyMapper(transaction)"
                    class="bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-2 py-1 rounded text-xxs font-medium"
                  >
                    Map Company
                  </button>
                </div>
                <div v-if="transaction.company_id" class="text-xxs text-gray-400 mt-1">ID: {{ transaction.company_id }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div v-if="transaction.customer_email" class="text-gray-900">{{ transaction.customer_email }}</div>
                <div v-else-if="transaction.matched_user?.email" class="text-gray-900">
                  {{ transaction.matched_user.email }}
                  <span class="text-xxs text-gray-400">(from user)</span>
                </div>
                <div v-else-if="transaction.matched_user_id" class="text-yellow-600 text-xxs">
                  User ID: {{ transaction.matched_user_id }} (email unknown)
                </div>
                <div v-else class="text-gray-400 italic">
                  N/A
                  <button
                    v-if="transaction.stripe_id"
                    @click.stop="enrichCustomerData(transaction)"
                    class="ml-2 text-xxs text-blue-600 hover:text-blue-800 underline"
                    title="Fetch customer data from Stripe"
                  >
                    Fetch from Stripe
                  </button>
                </div>
                <div v-if="transaction.customer_name" class="text-gray-500">{{ transaction.customer_name }}</div>
                <div v-else-if="transaction.matched_user?.name" class="text-gray-500">{{ transaction.matched_user.name }}</div>
                <div v-if="transaction.matched_user_id && !transaction.customer_email" class="text-green-600 text-xxs mt-1">
                  Matched User ID: {{ transaction.matched_user_id }}
                </div>
              </td>
              <td class="px-6 py-4 text-xs text-gray-900">
                <div class="max-w-xs truncate" :title="transaction.product_description || 'N/A'">
                  {{ transaction.product_description || 'N/A' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                <div class="text-xxs">{{ transaction.payment_method || 'N/A' }}</div>
                <div class="text-xxs text-gray-400">{{ transaction.currency?.toUpperCase() || 'EUR' }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div class="text-xxs text-gray-600">{{ transaction.source || 'manual' }}</div>
                <div class="mt-1">
                  <span
                    class="inline-flex items-center rounded px-2 py-0.5 text-xxs font-medium"
                    :class="isReportableSale(transaction) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                  >
                    {{ isReportableSale(transaction) ? 'Reportable' : 'Not reportable' }}
                  </span>
                </div>
                <div v-if="transaction.status" class="text-xxs" :class="{
                  'text-green-600': transaction.status === 'completed',
                  'text-yellow-600': transaction.status === 'pending',
                  'text-red-600': transaction.status === 'failed'
                }">
                  {{ transaction.status }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div v-if="!isReportableSale(transaction)" class="text-xxs text-gray-400">Not reportable</div>
                <div v-if="isReportableSale(transaction) && getPremierPartnerMapping(transaction)">
                  <commission-mapping
                    :mapping="getPremierPartnerMapping(transaction)"
                    :affiliate-companies="affiliateCompanies"
                    :routes="routes"
                    commission-type="premier_partner"
                    @mapped="handleMappingChanged"
                  />
                </div>
                <div v-else-if="isReportableSale(transaction)" class="text-xxs text-gray-400">No mapping</div>
                <button
                  v-if="canAutoMapPremierPartner(transaction)"
                  @click.stop="mapPremierPartner(transaction)"
                  :disabled="isMappingPremierPartner(transaction)"
                  class="mt-2 inline-flex items-center rounded bg-blue-600 px-2 py-1 text-xxs font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {{ isMappingPremierPartner(transaction) ? 'Mapping...' : 'Auto-map' }}
                </button>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-xs">
                <div v-if="!isReportableSale(transaction)" class="text-xxs text-gray-400">Not reportable</div>
                <commission-mapping
                  v-else-if="getBusinessPartnerMapping(transaction)"
                  :mapping="getBusinessPartnerMapping(transaction)"
                  :affiliate-companies="affiliateCompanies"
                  :routes="routes"
                  commission-type="business_partner"
                  @mapped="handleMappingChanged"
                />
                <div v-else class="text-xxs text-gray-400">No mapping</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="px-6 py-4 border-t border-gray-200">
        <nav class="flex items-center justify-center space-x-2">
          <button
            @click="goToPage(pagination.current_page - 1)"
            :disabled="!pagination.prev_page_url"
            :class="[
              'px-3 py-2 rounded-lg text-xs font-medium',
              pagination.prev_page_url
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            ]"
          >
            Previous
          </button>

          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-2 rounded-lg text-xs font-medium',
              page === pagination.current_page
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            ]"
          >
            {{ page }}
          </button>

          <button
            @click="goToPage(pagination.current_page + 1)"
            :disabled="!pagination.next_page_url"
            :class="[
              'px-3 py-2 rounded-lg text-xs font-medium',
              pagination.next_page_url
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            ]"
          >
            Next
          </button>
        </nav>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import CommissionMapping from './CommissionMapping.vue'

const props = defineProps({
  initialTransactions: Array,
  pagination: Object,
  stats: Object,
  companies: Array,
  affiliateCompanies: Array,
  filters: Object,
  routes: Object
})

const normalizeReportableOnlyFilter = (value) => {
  if (value === undefined || value === null || value === '') return true
  return value === true || value === 1 || value === '1' || value === 'true'
}

// Reactive state
const transactions = ref(props.initialTransactions)
const activeRowIndex = ref(-1)
const localFilters = ref({
  ...props.filters,
  reportable_only: normalizeReportableOnlyFilter(props.filters?.reportable_only)
})
const syncLoading = ref(false)
const enrichLoading = ref(false)
const backfillingSales = ref({})
const mappingPremierPartners = ref({})

// Computed properties

const visiblePages = computed(() => {
  const current = props.pagination.current_page
  const total = props.pagination.last_page
  const delta = 2

  const range = []
  const rangeWithDots = []

  for (let i = Math.max(2, current - delta);
       i <= Math.min(total - 1, current + delta);
       i++) {
    range.push(i)
  }

  if (current - delta > 2) {
    rangeWithDots.push(1, '...')
  } else {
    rangeWithDots.push(1)
  }

  rangeWithDots.push(...range)

  if (current + delta < total - 1) {
    rangeWithDots.push('...', total)
  } else if (total > 1) {
    rangeWithDots.push(total)
  }

  return rangeWithDots.filter(page => page !== 1 || total === 1)
})

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

const formatAmount = (cents, currency) => {
  const amount = cents / 100
  return `${formatCurrency(amount)} ${currency.toUpperCase()}`
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}


const setActiveRow = (index) => {
  activeRowIndex.value = index
}

const getPremierPartnerMapping = (transaction) => {
  return transaction.commission_mappings?.find(m => m.commission_type === 'premier_partner')
}

const getBusinessPartnerMapping = (transaction) => {
  return transaction.commission_mappings?.find(m => m.commission_type === 'business_partner')
}

const getTotalCommissionAmount = (transaction) => {
  if (!transaction.commission_mappings) return 0
  return transaction.commission_mappings.reduce((sum, mapping) => {
    return sum + (mapping.commission_amount_cents || 0)
  }, 0)
}

const isReportableSale = (transaction) => {
  return transaction.is_reportable_sale === true || transaction.is_reportable_sale === 1 || transaction.is_reportable_sale === '1'
}

const hasAffiliateMapping = (mapping) => {
  return Boolean(mapping?.affiliate_company_id || mapping?.affiliate_user_id)
}

const canAutoMapPremierPartner = (transaction) => {
  const premierMapping = getPremierPartnerMapping(transaction)
  const businessMapping = getBusinessPartnerMapping(transaction)
  return Boolean(transaction.company && (
    (premierMapping && !hasAffiliateMapping(premierMapping)) ||
    (businessMapping && !hasAffiliateMapping(businessMapping))
  ))
}

const isMappingPremierPartner = (transaction) => {
  return Boolean(mappingPremierPartners.value[transaction.id])
}

const mapPremierPartner = async (transaction) => {
  if (!transaction.company) {
    alert('Transaction must be mapped to a company first')
    return
  }

  mappingPremierPartners.value = {
    ...mappingPremierPartners.value,
    [transaction.id]: true
  }

  try {
    const response = await axios.post(props.routes.mapPremierPartner, {
      transaction_id: transaction.id
    })

    if (response.data.success) {
      if (response.data.transaction) {
        replaceTransaction(response.data.transaction)
      }
    } else {
      alert('Failed: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error mapping Premier Partner:', error)
    if (error.response?.data?.message) {
      alert('Error: ' + error.response.data.message)
    } else {
      alert('Error mapping Premier Partner')
    }
  } finally {
    const nextMapping = { ...mappingPremierPartners.value }
    delete nextMapping[transaction.id]
    mappingPremierPartners.value = nextMapping
  }
}

const enrichCustomerData = async (transaction) => {
  if (!transaction.stripe_id) {
    alert('Transaction has no Stripe ID')
    return
  }

  if (!confirm(`Fetch customer data from Stripe for transaction ${transaction.stripe_id}?`)) {
    return
  }

  try {
    const response = await axios.post('/admin/commission-tracking/enrich-customer', {
      transaction_id: transaction.id
    })

    if (response.data.success) {
      alert(response.data.message)
      window.location.reload()
    } else {
      alert('Failed: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error enriching customer data:', error)
    if (error.response?.data?.message) {
      alert('Error: ' + error.response.data.message)
    } else {
      alert('Error fetching customer data from Stripe')
    }
  }
}

const isBackfillingSale = (transaction) => {
  return Boolean(backfillingSales.value[transaction.id])
}

const backfillSale = async (transaction) => {
  if (!transaction.stripe_id) {
    alert('Transaction has no Stripe ID')
    return
  }

  if (!confirm(`Backfill Stripe sale for transaction ${transaction.stripe_id}? This may hide duplicate Stripe rows from reporting.`)) {
    return
  }

  backfillingSales.value = {
    ...backfillingSales.value,
    [transaction.id]: true
  }

  try {
    const response = await axios.post(props.routes.backfillRow, {
      transaction_id: transaction.id
    })

    if (response.data.success) {
      alert(response.data.message)
      window.location.reload()
    } else {
      alert('Failed: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error backfilling Stripe sale:', error)
    if (error.response?.data?.message) {
      alert('Error: ' + error.response.data.message)
    } else {
      alert('Error backfilling Stripe sale')
    }
  } finally {
    const nextBackfilling = { ...backfillingSales.value }
    delete nextBackfilling[transaction.id]
    backfillingSales.value = nextBackfilling
  }
}

const enrichAllCustomers = async () => {
  if (!confirm('This will fetch customer data from Stripe for all transactions missing customer email. This may take a while. Continue?')) {
    return
  }

  enrichLoading.value = true

  try {
    const response = await axios.post('/admin/commission-tracking/enrich-all-customers')

    if (response.data.success) {
      alert(`Successfully enriched ${response.data.enriched_count} transactions. ${response.data.skipped_count} were skipped.`)
      window.location.reload()
    } else {
      alert('Failed: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error enriching all customers:', error)
    if (error.response?.data?.message) {
      alert('Error: ' + error.response.data.message)
    } else {
      alert('Error fetching customer data from Stripe')
    }
  } finally {
    enrichLoading.value = false
  }
}

const handleMappingChanged = () => {
  window.location.reload()
}

const replaceTransaction = (updatedTransaction) => {
  transactions.value = transactions.value.map(transaction => (
    transaction.id === updatedTransaction.id ? updatedTransaction : transaction
  ))
}

const showCompanyMapper = async (transaction) => {
  // Debug: Check if the route is available
  if (!props.routes.mapCompany) {
    console.error('mapCompany route not available. Available routes:', props.routes)
    alert('Company mapping route not available. Check console for details.')
    return
  }

  const companyId = prompt(`Enter Company ID to map transaction ${transaction.stripe_id || transaction.id} to:`)
  if (companyId && !isNaN(companyId)) {
    try {
      const response = await axios.post(props.routes.mapCompany, {
        transaction_id: transaction.id,
        company_id: parseInt(companyId)
      })

      if (response.data.success) {
        if (response.data.transaction) {
          replaceTransaction(response.data.transaction)
        }
      } else {
        alert('Failed to map company: ' + response.data.message)
      }
    } catch (error) {
      console.error('Error mapping company:', error)
      if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message)
      } else {
        alert('Error mapping company to transaction')
      }
    }
  } else if (companyId !== null) {
    alert('Please enter a valid numeric Company ID')
  }
}

const suggestMapping = async (transaction) => {
  try {
    const response = await axios.post(props.routes.suggestMapping, {
      company_id: transaction.company_id
    })

    if (response.data.suggestions.premier_partner || response.data.suggestions.business_partner) {
      console.log('Suggestions:', response.data.suggestions)
      alert('Suggestions available - this feature needs more implementation')
    }
  } catch (error) {
    console.error('Error getting suggestions:', error)
  }
}


const manualSync = async () => {
  syncLoading.value = true

  try {
    const response = await axios.post(props.routes.manualSync)

    if (response.data.success) {
      alert(response.data.message)
      // Optionally reload the page after a short delay to show fresh data
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } else {
      alert('Failed to sync: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error syncing:', error)
    alert('Error syncing with Stripe')
  } finally {
    syncLoading.value = false
  }
}

const applyFilters = () => {
  const params = new URLSearchParams()

  Object.entries(localFilters.value).forEach(([key, value]) => {
    if (key === 'reportable_only') {
      params.append(key, value ? '1' : '0')
    } else if (value) {
      params.append(key, value)
    }
  })

  window.location.href = `${props.routes.index}?${params.toString()}`
}

const clearFilters = () => {
  window.location.href = props.routes.index
}

const getCompanyViewUrl = (companyId) => {
  return props.routes.companyView.replace(':id', companyId)
}

const goToPage = (page) => {
  const params = new URLSearchParams(window.location.search)
  params.set('page', page)
  window.location.href = `${props.routes.index}?${params.toString()}`
}

// Keyboard navigation
const handleKeyDown = (e) => {
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return

  switch (e.key) {
    case 'j':
      e.preventDefault()
      if (activeRowIndex.value < transactions.value.length - 1) {
        activeRowIndex.value++
      }
      break
    case 'k':
      e.preventDefault()
      if (activeRowIndex.value > 0) {
        activeRowIndex.value--
      } else if (activeRowIndex.value === -1 && transactions.value.length > 0) {
        activeRowIndex.value = transactions.value.length - 1
      }
      break
    case ' ':
      e.preventDefault()
      // Space key functionality removed (was for bulk selection)
      break
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>
